import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataGrid, type Column } from './DataGrid'
import { FilterBar, type FilterDef } from './FilterBar'
import { getJson, sendJson, fmtDate, fmtDateTime, money, type GridPrefs, type GridResult, type Lookups, type MemberGridRow, type MembershipGridRow, type InteractionGridRow, type SyncStatus } from './api'
import { StatusBadge, SyncBadge, useJson } from './common'

// The primary grids — Members, Memberships and Interactions — over the server-paged API, with
// the user's saved view (sort, page size, filters) in mbr.GridPref.

interface GridState { page: number; size: number; sort: string; dir: 'asc' | 'desc'; q: string; filters: Record<string, string>; includeInactive: boolean }

function useGridState(grid: 'members' | 'memberships' | 'interactions', defaults: { sort: string; dir: 'asc' | 'desc' }) {
  const [state, setState] = useState<GridState>({ page: 1, size: 30, sort: defaults.sort, dir: defaults.dir, q: '', filters: {}, includeInactive: false })
  const [prefsLoaded, setPrefsLoaded] = useState(false)
  const [savedAt, setSavedAt] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    getJson<{ prefs: GridPrefs | null; updated_at: string | null }>(`/api/membership/prefs/${grid}`)
      .then((p) => {
        if (!alive) return
        if (p.prefs) {
          setState((s) => ({
            ...s,
            sort: p.prefs?.sort ?? s.sort, dir: p.prefs?.dir ?? s.dir, size: p.prefs?.size ?? s.size,
            filters: p.prefs?.filters ?? s.filters, q: p.prefs?.q ?? s.q, includeInactive: p.prefs?.includeInactive ?? s.includeInactive,
          }))
          setSavedAt(p.updated_at)
        }
      })
      .catch(() => undefined)
      .finally(() => { if (alive) setPrefsLoaded(true) })
    return () => { alive = false }
  }, [grid])
  const save = useCallback(async () => {
    const prefs: GridPrefs = { sort: state.sort, dir: state.dir, size: state.size, filters: state.filters, q: state.q, includeInactive: state.includeInactive }
    await sendJson(`/api/membership/prefs/${grid}`, 'PUT', { prefs })
    setSavedAt(new Date().toISOString())
  }, [grid, state])
  return { state, setState, prefsLoaded, savedAt, save }
}

function gridUrl(base: string, s: GridState): string {
  const p = new URLSearchParams()
  p.set('page', String(s.page)); p.set('size', String(s.size)); p.set('sort', s.sort); p.set('dir', s.dir)
  if (s.q) p.set('q', s.q)
  if (s.includeInactive) p.set('f_inactive', '1')
  for (const [k, v] of Object.entries(s.filters)) if (v) p.set(`f_${k}`, v)
  return `${base}?${p.toString()}`
}

const opt = <T extends { n: number }>(rows: T[] | undefined, key: keyof T & string) =>
  (rows ?? [])
    .map((r) => ({ v: (r as unknown as Record<string, unknown>)[key], n: r.n }))
    .filter((x) => x.v !== null && x.v !== undefined && x.v !== '')
    .map((x) => ({ value: String(x.v), label: `${String(x.v)} (${x.n.toLocaleString('en-GB')})` }))

const YES_NO = [{ value: '1', label: 'Yes' }, { value: '0', label: 'No' }]
const SYNC_OPTS = [
  { value: 'in_sync', label: 'In sync' }, { value: 'discrepancies', label: 'Discrepancies' }, { value: 'local_only', label: 'Local only' },
  { value: 'deleted_in_ontrack', label: 'Deleted in Ontrack' }, { value: 'never_pulled', label: 'Never pulled' },
]
const MASTER_OPTS = [{ value: 'none', label: 'No master' }, { value: 'ontrack', label: 'Ontrack' }, { value: 'donorfy', label: 'Donorfy' }, { value: 'membership', label: 'Membership' }]

// ---------------------------------------------------------------------------
export function MembersView({ lookups }: { lookups: Lookups | null }) {
  const nav = useNavigate()
  const { state, setState, prefsLoaded, savedAt, save } = useGridState('members', { sort: 'surname', dir: 'asc' })
  const url = useMemo(() => (prefsLoaded ? gridUrl('/api/membership/members', state) : null), [state, prefsLoaded])
  const grid = useJson<GridResult<MemberGridRow>>(url)

  const defs: FilterDef[] = useMemo(() => [
    { key: 'status', label: 'Status', options: opt(lookups?.statuses, 'status') },
    { key: 'category', label: 'Membership', options: opt(lookups?.categories, 'category'), width: 200 },
    { key: 'type', label: 'Type', options: opt(lookups?.types, 'type') },
    { key: 'class', label: 'Class', options: opt(lookups?.classes, 'class') },
    { key: 'payment_type', label: 'Payment type', options: opt(lookups?.paymentTypes, 'payment_type') },
    { key: 'sync_status', label: 'Sync status', options: SYNC_OPTS },
    { key: 'master', label: 'Master', options: MASTER_OPTS },
    { key: 'life', label: 'Life member', options: YES_NO },
    { key: 'gift_aid', label: 'Gift Aid', options: YES_NO },
    { key: 'has_email', label: 'Has email', options: YES_NO },
    { key: 'has_gocardless', label: 'GoCardless paired', options: YES_NO },
    { key: 'postcode', label: 'Postcode starts', kind: 'text', placeholder: 'e.g. YO18', width: 110 },
    { key: 'renewal_from', label: 'Renewal from', kind: 'date' },
    { key: 'renewal_to', label: 'Renewal to', kind: 'date' },
  ], [lookups])

  const columns: Column<MemberGridRow>[] = useMemo(() => [
    { key: 'surname', label: 'Surname' },
    { key: 'first_name', label: 'First name' },
    { key: 'membership_card_number', label: 'Card no' },
    { key: 'membership_number', label: 'Membership no', align: 'right' },
    { key: 'category', label: 'Membership' },
    { key: 'membership_type', label: 'Type' },
    { key: 'postcode', label: 'Postcode' },
    { key: 'email', label: 'Email', className: 'wrap' },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'telephone', label: 'Telephone', render: (r) => r.telephone || r.mobile || '' },
    { key: 'renewal_date', label: 'Renewal', render: (r) => fmtDate(r.renewal_date) },
    { key: 'sync_status', label: 'Sync', render: (r) => <SyncBadge status={r.sync_status} discrepancies={r.discrepancies} /> },
  ], [])

  return (
    <div className="mbr">
      <FilterBar q={state.q} onQ={(q) => setState((s) => ({ ...s, q, page: 1 }))} defs={defs} values={state.filters}
        onChange={(k, v) => setState((s) => ({ ...s, page: 1, filters: { ...s.filters, [k]: v } }))}
        includeInactive={state.includeInactive} onIncludeInactive={(v) => setState((s) => ({ ...s, includeInactive: v, page: 1 }))}
        onSaveView={save} onReset={() => setState((s) => ({ ...s, q: '', filters: {}, page: 1 }))} savedAt={savedAt}
        searchPlaceholder="Surname, first name, card no, membership no, email, postcode, town or phone" />
      <DataGrid<MemberGridRow> columns={columns} rows={grid.data?.rows ?? []} total={grid.data?.total ?? 0} page={state.page} size={state.size}
        sort={state.sort} dir={state.dir} loading={grid.loading} error={grid.error} rowKey={(r) => r.member_id}
        rowClass={(r) => (r.inactive ? 'mbr-row-inactive' : '')}
        onSort={(key, dir) => setState((s) => ({ ...s, sort: key, dir, page: 1 }))}
        onPage={(page) => setState((s) => ({ ...s, page }))} onSize={(size) => setState((s) => ({ ...s, size, page: 1 }))}
        onRowClick={(r) => nav(`/membership/members/${r.member_id}`)}
        emptyText={prefsLoaded ? 'No members match. If the tab is new, press Sync on the Integrations tab to pull Ontrack.' : 'Loading your view…'} />
    </div>
  )
}

// ---------------------------------------------------------------------------
export function MembershipsView({ lookups }: { lookups: Lookups | null }) {
  const nav = useNavigate()
  const { state, setState, prefsLoaded, savedAt, save } = useGridState('memberships', { sort: 'membership_number', dir: 'desc' })
  const url = useMemo(() => (prefsLoaded ? gridUrl('/api/membership/memberships', state) : null), [state, prefsLoaded])
  const grid = useJson<GridResult<MembershipGridRow>>(url)

  const defs: FilterDef[] = useMemo(() => [
    { key: 'status', label: 'Status', options: opt(lookups?.membershipStatuses, 'status') },
    { key: 'category', label: 'Category', options: opt(lookups?.categories, 'category'), width: 200 },
    { key: 'type', label: 'Type', options: opt(lookups?.types, 'type') },
    { key: 'class', label: 'Class', options: opt(lookups?.classes, 'class') },
    { key: 'source', label: 'Source', options: opt(lookups?.sources, 'source') },
    { key: 'payment_type', label: 'Payment type', options: opt(lookups?.paymentTypes, 'payment_type') },
    { key: 'has_dd', label: 'Direct debit', options: YES_NO },
    { key: 'gift_aid', label: 'Gift Aid', options: YES_NO },
    { key: 'members', label: 'Members', options: [{ value: '1', label: 'One' }, { value: '2', label: 'Two or more (joint)' }, { value: '0', label: 'None' }] },
    { key: 'sync_status', label: 'Sync status', options: SYNC_OPTS },
    { key: 'master', label: 'Master', options: MASTER_OPTS },
    { key: 'postcode', label: 'Postcode starts', kind: 'text', placeholder: 'e.g. YO18', width: 110 },
    { key: 'renewal_from', label: 'Renewal from', kind: 'date' },
    { key: 'renewal_to', label: 'Renewal to', kind: 'date' },
  ], [lookups])

  const columns: Column<MembershipGridRow>[] = useMemo(() => [
    { key: 'membership_number', label: 'Membership no', align: 'right' },
    { key: 'members_names', label: 'Members', className: 'wrap' },
    { key: 'category', label: 'Category' },
    { key: 'type', label: 'Type' },
    { key: 'class', label: 'Class' },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'renewal_date', label: 'Renewal', render: (r) => fmtDate(r.renewal_date) },
    { key: 'last_renewed', label: 'Last renewed', render: (r) => fmtDate(r.last_renewed) },
    { key: 'payment_type', label: 'Payment type' },
    { key: 'has_dd', label: 'DD', sortable: false, render: (r) => (r.has_dd ? 'Yes' : '') },
    { key: 'member_count', label: 'People', align: 'right' },
    { key: 'postcode', label: 'Postcode' },
    { key: 'sync_status', label: 'Sync', render: (r) => <SyncBadge status={r.sync_status} discrepancies={r.discrepancies} /> },
  ], [])

  return (
    <div className="mbr">
      <FilterBar q={state.q} onQ={(q) => setState((s) => ({ ...s, q, page: 1 }))} defs={defs} values={state.filters}
        onChange={(k, v) => setState((s) => ({ ...s, page: 1, filters: { ...s.filters, [k]: v } }))}
        includeInactive={state.includeInactive} onIncludeInactive={(v) => setState((s) => ({ ...s, includeInactive: v, page: 1 }))}
        inactiveLabel="Include lapsed, cancelled and test memberships"
        onSaveView={save} onReset={() => setState((s) => ({ ...s, q: '', filters: {}, page: 1 }))} savedAt={savedAt}
        searchPlaceholder="Membership no, members' names, postcode, town or category" />
      <DataGrid<MembershipGridRow> columns={columns} rows={grid.data?.rows ?? []} total={grid.data?.total ?? 0} page={state.page} size={state.size}
        sort={state.sort} dir={state.dir} loading={grid.loading} error={grid.error} rowKey={(r) => r.membership_id}
        rowClass={(r) => (r.inactive ? 'mbr-row-inactive' : '')}
        onSort={(key, dir) => setState((s) => ({ ...s, sort: key, dir, page: 1 }))}
        onPage={(page) => setState((s) => ({ ...s, page }))} onSize={(size) => setState((s) => ({ ...s, size, page: 1 }))}
        onRowClick={(r) => nav(`/membership/memberships/${r.membership_id}`)}
        emptyText={prefsLoaded ? 'No memberships match.' : 'Loading your view…'} />
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Interactions — membership-card interactions at the tills (reports db/130), synced from
//  MERAC by Sync Hub Refresh-MembershipInteractions. Read-only; a "Refresh now" button
//  queues app.RefreshRequests kind 'interactions' and reloads the grid when it finishes.
// ---------------------------------------------------------------------------
function InteractionsRefresh({ status, reloadStatus, onRefreshed }: { status: SyncStatus | null; reloadStatus: () => void; onRefreshed: () => void }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const run = async () => {
    setBusy(true); setMsg(null)
    try {
      const s = await sendJson<SyncStatus & { already_in_flight?: boolean }>('/api/membership/sync/interactions', 'POST')
      reloadStatus()
      if (s.already_in_flight) setMsg('A refresh is already running.')
      let tries = 0
      const tick = async () => {
        tries++
        let cur: SyncStatus | null = null
        let readErr: string | null = null
        try { cur = await getJson<SyncStatus>('/api/membership/sync/interactions') } catch (e) { readErr = String((e as Error)?.message ?? e) }
        reloadStatus()
        if (cur && !cur.in_flight) {
          if (cur.latest?.status === 'error') { const m = cur.latest?.message; setMsg(m ? `Refresh failed: ${m}` : 'Refresh failed') }
          else { setMsg('Refreshed.'); onRefreshed() }
          return
        }
        if (readErr && tries >= 3) { setMsg(`Couldn't read the refresh status: ${readErr}`); return }
        if (tries < 12) setTimeout(() => void tick(), 3000)
        else setMsg('Still refreshing — it will appear shortly.')
      }
      setTimeout(() => void tick(), 3000)
    } catch (e) { setMsg(String((e as Error)?.message ?? e)) }
    finally { setBusy(false) }
  }
  const inFlight = status?.in_flight
  const disabled = busy || inFlight || (status?.locked ?? false)
  return (
    <span className="mbr-download">
      <button type="button" className="btn-secondary" disabled={disabled} onClick={() => void run()}
        title={status?.locked && status.next_available_at ? `Available again at ${fmtDateTime(status.next_available_at)}` : 'Re-run the MERAC pull and reload the grid'}>
        {inFlight ? 'Refreshing…' : busy ? 'Queuing…' : 'Refresh now'}
      </button>
      {status?.last_refresh && <span className="mbr-note"> Last synced {fmtDateTime(status.last_refresh)}</span>}
      {msg && <span className="mbr-note"> {msg}</span>}
    </span>
  )
}

export function InteractionsView({ lookups, reloadLookups }: { lookups: Lookups | null; reloadLookups: () => void }) {
  const { state, setState, prefsLoaded, savedAt, save } = useGridState('interactions', { sort: 'visited_at', dir: 'desc' })
  const url = useMemo(() => (prefsLoaded ? gridUrl('/api/membership/interactions', state) : null), [state, prefsLoaded])
  const grid = useJson<GridResult<InteractionGridRow>>(url)
  const sync = useJson<SyncStatus>('/api/membership/sync/interactions')
  const neverSynced = sync.data ? (sync.data.last_refresh == null || sync.data.last_refresh_status === 'never') : false
  const emptyText = !prefsLoaded ? 'Loading your view…'
    : neverSynced ? "No interactions synced yet — press Refresh now, or they'll arrive with tonight's sync."
    : 'No interactions match.'

  const defs: FilterDef[] = useMemo(() => [
    { key: 'membership_no', label: 'Membership no', kind: 'text', placeholder: 'number', width: 110 },
    { key: 'card_no', label: 'Card no', kind: 'text', placeholder: 'contains', width: 120 },
    { key: 'member', label: 'Member', kind: 'text', placeholder: 'name contains', width: 150 },
    { key: 'category', label: 'Category', options: opt(lookups?.interactionCategories, 'category'), width: 180 },
    { key: 'item', label: 'Item', kind: 'text', placeholder: 'contains', width: 150 },
    { key: 'employee', label: 'Served by', options: opt(lookups?.interactionEmployees, 'employee_name'), width: 150 },
    { key: 'till_location', label: 'Location', options: opt(lookups?.interactionLocations, 'till_location'), width: 160 },
    { key: 'date_from', label: 'Date from', kind: 'date' },
    { key: 'date_to', label: 'Date to', kind: 'date' },
    { key: 'expiry_from', label: 'Expiry from', kind: 'date' },
    { key: 'expiry_to', label: 'Expiry to', kind: 'date' },
    { key: 'txn', label: 'Txn', kind: 'text', placeholder: 'number', width: 90 },
  ], [lookups])

  const columns: Column<InteractionGridRow>[] = useMemo(() => [
    { key: 'membership_no', label: 'Membership no' },
    { key: 'card_no', label: 'Card no' },
    { key: 'member_name', label: 'Member', className: 'wrap' },
    { key: 'category', label: 'Category' },
    { key: 'visited_at', label: 'Date/time', render: (r) => fmtDateTime(r.visited_at) },
    { key: 'item', label: 'Item', className: 'wrap' },
    { key: 'qty', label: 'Qty', align: 'right' },
    { key: 'rrp', label: 'RRP', align: 'right', render: (r) => money(r.rrp) },
    { key: 'sold_price', label: 'Sold at', align: 'right', render: (r) => money(r.sold_price) },
    { key: 'till_location', label: 'Location' },
    { key: 'employee_name', label: 'Served by' },
    { key: 'membership_expiry', label: 'Expiry', render: (r) => fmtDate(r.membership_expiry) },
    { key: 'transaction_no', label: 'Txn', align: 'right' },
  ], [])

  return (
    <div className="mbr">
      <div className="mbr-head">
        <p className="budget-sub">Every membership-card interaction at the tills for last year, this year and next — synced from MERAC. <InteractionsRefresh status={sync.data} reloadStatus={() => sync.reload()} onRefreshed={() => { grid.reload(); sync.reload(); reloadLookups() }} /></p>
      </div>
      <FilterBar q={state.q} onQ={(q) => setState((s) => ({ ...s, q, page: 1 }))} defs={defs} values={state.filters}
        onChange={(k, v) => setState((s) => ({ ...s, page: 1, filters: { ...s.filters, [k]: v } }))}
        includeInactive={state.includeInactive} onIncludeInactive={(v) => setState((s) => ({ ...s, includeInactive: v, page: 1 }))}
        showInactive={false}
        onSaveView={save} onReset={() => setState((s) => ({ ...s, q: '', filters: {}, page: 1 }))} savedAt={savedAt}
        searchPlaceholder="Member, card number or item" />
      <DataGrid<InteractionGridRow> columns={columns} rows={grid.data?.rows ?? []} total={grid.data?.total ?? 0} page={state.page} size={state.size}
        sort={state.sort} dir={state.dir} loading={grid.loading} error={grid.error} rowKey={(r) => r.interaction_id}
        onSort={(key, dir) => setState((s) => ({ ...s, sort: key, dir, page: 1 }))}
        onPage={(page) => setState((s) => ({ ...s, page }))} onSize={(size) => setState((s) => ({ ...s, size, page: 1 }))}
        allowAll emptyText={emptyText} />
    </div>
  )
}
